import { useState } from "react"
import type { editor } from "monaco-editor"
import Select from "@/v2-ui/select"
import Button from "@/v2-backoffice-client/components/button"
import { applyInspectorEdit } from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.utils"
import {
  buildClassBreaksRendererValue,
  buildSimpleRendererValue,
  getGeometryKind,
  isNumericFieldType,
} from "@/v2-backoffice-client/layer/update/inspector/layer.update.inspector.guided.utils"
import type { LayerServiceMetadata } from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerUpdateInspectorGuidedRendererProps = {
  editor: editor.IStandaloneCodeEditor
  metadata: LayerServiceMetadata
}

/**
 * Renderer presets for the service's geometry: a clean brand-green simple
 * symbol, or a five-class class-breaks starter over a numeric field (even
 * placeholder breaks — the preview shows the effect, the operator or the AI
 * tunes the values). Both write a complete root `renderer`.
 */
function LayerUpdateInspectorGuidedRenderer(
  props: LayerUpdateInspectorGuidedRendererProps,
) {
  const { editor: editorInstance, metadata } = props

  const numericFields = metadata.fields.filter((field) =>
    isNumericFieldType(field.type),
  )
  const [breaksField, setBreaksField] = useState<string>(
    numericFields[0]?.name ?? "",
  )

  const kind = getGeometryKind(metadata.geometryType)
  if (!kind) return null

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="outline"
        size="sm"
        className="justify-start"
        iconSrc="palette"
        title="A single clean brand-green symbol for every feature"
        data-feature-code="UI_BackofficeLayers_Inspector_RendererSimple"
        onClick={() => {
          applyInspectorEdit(
            editorInstance,
            ["renderer"],
            buildSimpleRendererValue(kind),
          )
        }}
      >
        Simple symbol
      </Button>

      {numericFields.length > 0 && (
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 justify-start"
            iconSrc="chart-histogram"
            title="Five green-ramp classes over the chosen numeric field; tune the break values in the editor afterwards"
            data-feature-code="UI_BackofficeLayers_Inspector_RendererBreaks"
            onClick={() => {
              if (breaksField) {
                applyInspectorEdit(
                  editorInstance,
                  ["renderer"],
                  buildClassBreaksRendererValue(kind, breaksField),
                )
              }
            }}
          >
            Class breaks
          </Button>
          <Select value={breaksField} onValueChange={setBreaksField}>
            <Select.Trigger
              size="xs"
              className="h-7 min-w-0 flex-1 text-[11px]"
              aria-label="Class-breaks field"
            >
              <Select.Value placeholder="Field" />
            </Select.Trigger>
            <Select.Content>
              {numericFields.map((field) => (
                <Select.Item key={field.name} value={field.name}>
                  {field.alias || field.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
      )}
    </div>
  )
}

export default LayerUpdateInspectorGuidedRenderer
